import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import pb from '../lib/pb';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from "react-router-dom";
import MusicPlayer from "../components/MusicPlayer";
import VideoPlayer from "../components/VideoPlayer";
import { getMediaUrl, getPbUrl, firstFile } from '../lib/media';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import RepeatRoundedIcon from '@mui/icons-material/RepeatRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import FullscreenRoundedIcon from '@mui/icons-material/FullscreenRounded';
import FullscreenExitRoundedIcon from '@mui/icons-material/FullscreenExitRounded';
import VolumeOffRoundedIcon from '@mui/icons-material/VolumeOffRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded';
import ShoppingBagRoundedIcon from '@mui/icons-material/ShoppingBagRounded';

/* ------------------------------------------------------------------ */
/* Shared styles: theme tokens + base + buttons + the post card         */
/* (Feed.js and CreatePost.js rely on these through the .mf-feed class) */
/* ------------------------------------------------------------------ */
const POST_CSS = `
/* ---------- Theme tokens: DARK is the default ---------- */
.mf-feed{
  --mf-bg:#0b0d12; --mf-bg-top:#0f1218;
  --mf-surface:#141821; --mf-surface-2:#1a1f2a; --mf-surface-3:#232a39; --mf-tab-active:#2a3243;
  --mf-border:#232938; --mf-border-strong:#323b4e;
  --mf-text:#eceff6; --mf-muted:#9aa4b8; --mf-faint:#6c768b;
  --mf-accent:#ff5d7d; --mf-grad:linear-gradient(135deg,#2196f3 0%,#f8663f 100%);
  --mf-accent-soft:rgba(255,93,125,.14); --mf-glow:rgba(255,93,125,.08);
  --mf-success:#3ddc97; --mf-success-soft:rgba(61,220,151,.12);
  --mf-shadow:0 1px 0 rgba(255,255,255,.035) inset,0 10px 28px rgba(0,0,0,.35);
  --mf-overlay:rgba(4,6,10,.72);
  color-scheme:dark;
}
.mf-feed[data-mf-theme="light"]{
  --mf-bg:#f3f4f8; --mf-bg-top:#ffffff;
  --mf-surface:#ffffff; --mf-surface-2:#f1f3f8; --mf-surface-3:#e7eaf2; --mf-tab-active:#ffffff;
  --mf-border:#e3e6ee; --mf-border-strong:#d1d7e3;
  --mf-text:#141826; --mf-muted:#586177; --mf-faint:#8791a5;
  --mf-accent:#e5365a; --mf-grad:linear-gradient(135deg,#e93559 0%,#f2603a 100%);
  --mf-accent-soft:rgba(229,54,90,.10); --mf-glow:rgba(229,54,90,.07);
  --mf-success:#12a06a; --mf-success-soft:rgba(18,160,106,.10);
  --mf-shadow:0 1px 2px rgba(16,24,40,.06),0 10px 28px rgba(16,24,40,.07);
  --mf-overlay:rgba(15,18,28,.5);
  color-scheme:light;
}

/* ---------- Base ---------- */
.mf-feed{
  font-family:Inter,ui-sans-serif,system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif;
  line-height:1.5; -webkit-font-smoothing:antialiased;
}
.mf-feed *,.mf-feed *::before,.mf-feed *::after{box-sizing:border-box}
.mf-feed a{color:inherit;text-decoration:none}
.mf-feed button{font-family:inherit}
.mf-feed button:focus-visible,.mf-feed a:focus-visible{outline:2px solid var(--mf-accent);outline-offset:2px}
.mf-feed ::selection{background:var(--mf-accent-soft)}
.mf-feed ::-webkit-scrollbar{width:10px;height:10px}
.mf-feed ::-webkit-scrollbar-thumb{background:var(--mf-border-strong);border-radius:10px;border:2px solid transparent;background-clip:content-box}

/* ---------- Shared buttons + input ---------- */
.mf-btn{display:inline-flex;align-items:center;justify-content:center;gap:6px;height:38px;padding:0 16px;border-radius:12px;
  border:1px solid var(--mf-border-strong);background:var(--mf-surface-2);color:var(--mf-text);font-weight:700;font-size:14px;
  cursor:pointer;white-space:nowrap;text-decoration:none;transition:background-color .15s,border-color .15s,filter .15s,transform .1s}
.mf-btn:hover{background:var(--mf-surface-3)}
.mf-btn:active{transform:scale(.98)}
.mf-btn--ghost{background:transparent}
.mf-btn--primary{background:var(--mf-grad);border-color:transparent;color:#fff}
.mf-btn--primary:hover{background:var(--mf-grad);filter:brightness(1.08)}
.mf-btn.is-on{background:var(--mf-accent-soft);border-color:var(--mf-accent);color:var(--mf-accent)}
.mf-btn:disabled{opacity:.5;cursor:not-allowed}
.mf-btn--sm{height:34px;padding:0 12px;font-size:13px;border-radius:10px}
.mf-feed a.mf-btn--primary{color:#fff}

.mf-input{flex:1;min-width:0;height:42px;border-radius:999px;border:1px solid var(--mf-border-strong);background:var(--mf-surface-2);
  color:var(--mf-text);padding:0 16px;font-size:16px;font-family:inherit;outline:none;transition:border-color .15s,box-shadow .15s}
.mf-input::placeholder{color:var(--mf-faint)}
.mf-input:focus{border-color:var(--mf-accent);box-shadow:0 0 0 3px var(--mf-accent-soft)}

.mf-textarea{width:100%;min-height:96px;resize:vertical;border-radius:14px;border:1px solid var(--mf-border-strong);background:var(--mf-surface-2);
  color:var(--mf-text);padding:12px 14px;font:inherit;font-size:16px;outline:none}
.mf-textarea::placeholder{color:var(--mf-faint)}
.mf-textarea:focus{border-color:var(--mf-accent);box-shadow:0 0 0 3px var(--mf-accent-soft)}

/* Avatar */
.mf-avatar{flex:none;display:inline-grid;place-items:center;border-radius:50%;overflow:hidden;background:var(--mf-grad);color:#fff;
  font-weight:800;user-select:none;box-shadow:0 0 0 2px var(--mf-surface),0 0 0 3.5px var(--mf-border-strong)}
.mf-avatar img{width:100%;height:100%;object-fit:cover;display:block}

/* ---------- Card ----------
   The post is a FRAME: its padding shows the post's background colour on all
   four sides. The "stage" inside it holds the content and fills the whole
   available viewport (viewport height minus the sticky top bar). Everything
   else is an overlay on top of the stage, so the card keeps one fixed height.
   Overlay colours are fixed (white on dark) so they stay readable on any media
   and on any chosen card colour.
   Tips: --mf-frame (frame thickness), --mf-post-r / --mf-slide-r (corner radius),
   --mf-slide-w (width of each carousel card), --mf-media-fit (cover | contain),
   --mf-footer-h (height of your bottom bar) can all be set on .mf-feed. */
.mf-post{--mf-frame:10px;--mf-post-r:28px;--mf-slide-r:20px;
  position:relative;margin:0;padding:var(--mf-frame);display:flex;
  height:calc(100vh - var(--mf-topbar-h,64px));min-height:340px;
  background:var(--mf-surface);color:var(--mf-text);border:1px solid var(--mf-border);border-radius:var(--mf-post-r);
  box-shadow:var(--mf-shadow);overflow:hidden;transition:border-color .2s,background-color .25s}
@supports (height:100dvh){.mf-post{height:calc(100dvh - var(--mf-topbar-h,64px))}}
.mf-post:hover,.mf-post.is-active{border-color:var(--mf-border-strong)}

.mf-stage{--mf-bar-h:52px;--mf-dots-h:0px;
  --mf-fb:max(0px,calc(var(--mf-footer-h,84px) - var(--mf-frame,10px) - 1px));
  --mf-stack:calc(var(--mf-fb) + var(--mf-bar-h) + 22px + var(--mf-dots-h));
  position:relative;isolation:isolate;overflow:hidden;flex:1 1 auto;min-width:0;width:100%;height:100%;
  border-radius:var(--mf-slide-r);background:transparent;container-type:inline-size;container-name:mfstage}
.mf-stage.has-dots{--mf-dots-h:24px}

/* Dark pill used by the view count + the Content Only button */
.mf-pill{display:inline-flex;align-items:center;gap:6px;height:32px;padding:0 12px;border-radius:999px;border:0;background:rgba(0,0,0,.62);
  color:#fff;font:inherit;font-size:13px;font-weight:700;line-height:1;white-space:nowrap}
.mf-pill svg{font-size:18px}
button.mf-pill{cursor:pointer;transition:background-color .15s}
button.mf-pill:hover{background:rgba(0,0,0,.82)}
.mf-views{padding:0 12px 0 10px}

.mf-top{position:absolute;z-index:6;top:14px;left:12px;right:12px;display:flex;align-items:center;justify-content:space-between;gap:8px;pointer-events:none}
.mf-top>*{pointer-events:auto}
.mf-co{animation:mf-rise .22s ease-out both}

/* Text-only / locked posts: the text itself is the content (shown on a rounded card) */
.mf-stage-center{position:absolute;inset:0;z-index:1;display:flex;overflow:auto;border-radius:var(--mf-slide-r);background:var(--mf-surface-2);
  padding:64px 22px calc(var(--mf-stack) + 8px)}
.mf-stage-text{margin:auto;width:100%;max-width:900px;text-align:center;font-size:clamp(22px,3.4vw,38px);line-height:1.4;font-weight:700;
  white-space:pre-wrap;overflow-wrap:anywhere;color:var(--mf-text)}
.mf-stage-empty{margin:auto;color:var(--mf-faint);font-size:15px}
.mf-locked{display:flex;align-items:center;gap:14px;padding:18px;border-radius:14px;color:var(--mf-muted);
  border:1px dashed var(--mf-border-strong);
  background:repeating-linear-gradient(135deg,var(--mf-surface-2) 0 12px,var(--mf-surface) 12px 24px)}
.mf-stage-center .mf-locked{margin:auto;max-width:420px}
.mf-locked svg{font-size:30px;color:var(--mf-accent);flex:none}
.mf-locked strong{display:block;color:var(--mf-text);font-size:15px}
.mf-locked span{font-size:13.5px}

/* Bottom bar: author (mf-post-head) on the left, credits on the right */
.mf-bar{position:absolute;z-index:6;left:10px;right:10px;bottom:calc(var(--mf-fb) + 10px);display:flex;align-items:center;
  justify-content:space-between;gap:8px;pointer-events:none}
.mf-bar>*{pointer-events:auto}

.mf-post-head{position:relative;display:flex;align-items:center;gap:8px;min-width:0;max-width:100%;padding:5px 8px 5px 5px;border-radius:999px;
  background:rgba(0,0,0,.62);color:#fff}
.mf-post-avatar{display:inline-flex;flex:none}
.mf-post-head .mf-avatar{box-shadow:0 0 0 2px rgba(255,255,255,.9)}
.mf-post-who{min-width:0;display:flex;flex-direction:column;gap:1px}
.mf-post-name{font-weight:700;font-size:14px;line-height:1.2;color:#fff;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
  text-decoration:none;max-width:200px}
a.mf-post-name:hover{color:#fff;opacity:.85}
.mf-post-time{font-size:11.5px;line-height:1.2;color:rgba(255,255,255,.74)}
.mf-badge{display:inline-flex;align-items:center;gap:4px;font-size:11.5px;font-weight:700;padding:3px 8px;border-radius:999px;
  background:rgba(255,255,255,.14);color:#fff;flex:none}
.mf-badge svg{font-size:14px}
.mf-badge--public{color:#6ee7b7}
.mf-badge--private{color:#ff9db1}

/* Owner menu (opens upward from the author pill) */
.mf-menu{flex:none}
.mf-menu-btn{width:30px;height:30px;border-radius:50%;border:0;background:transparent;color:#fff;display:grid;place-items:center;cursor:pointer;
  transition:background-color .15s}
.mf-menu-btn:hover,.mf-menu-btn[aria-expanded="true"]{background:rgba(255,255,255,.18)}
.mf-menu-pop{position:absolute;left:0;bottom:calc(100% + 8px);z-index:30;min-width:176px;padding:6px;border-radius:14px;background:var(--mf-surface);
  color:var(--mf-text);border:1px solid var(--mf-border-strong);box-shadow:0 16px 40px rgba(0,0,0,.4)}
.mf-menu-item{display:flex;align-items:center;gap:10px;width:100%;height:40px;padding:0 12px;border:0;border-radius:10px;background:transparent;
  color:var(--mf-text);font-weight:600;font-size:14px;cursor:pointer;text-align:left}
.mf-menu-item:hover{background:var(--mf-surface-2)}
.mf-menu-item svg{font-size:20px}
.mf-menu-item--danger{color:#ff6b6b}
.mf-menu-item--danger:hover{background:rgba(255,107,107,.12)}

/* Actions */
.mf-act{display:inline-flex;align-items:center;gap:6px;height:40px;padding:0 12px;border-radius:999px;border:0;background:transparent;
  color:var(--mf-muted);font-weight:700;font-size:14px;cursor:pointer;transition:background-color .15s,color .15s}
.mf-act:hover{background:var(--mf-surface-2);color:var(--mf-text)}
.mf-act svg{font-size:22px}
.mf-like.is-pop svg{animation:mf-pop .45s cubic-bezier(.2,.9,.3,1.4)}
@keyframes mf-pop{0%{transform:scale(1)}40%{transform:scale(1.35)}100%{transform:scale(1)}}
.mf-bar .mf-credits{flex:none;background:rgba(0,0,0,.62);color:#fff}
.mf-bar .mf-credits:hover{background:rgba(0,0,0,.82);color:#fff}
.mf-bar .mf-credits svg{color:#ffd166}
.mf-credits small{font-size:12px;font-weight:700;opacity:.85}

/* Right-hand action rail (only while the card is centered) */
.mf-rail{position:absolute;z-index:6;right:10px;bottom:var(--mf-stack);display:flex;
  flex-direction:column;gap:10px;animation:mf-rise .22s ease-out both}
.mf-rail .mf-act{flex-direction:column;gap:2px;width:56px;height:auto;padding:9px 0 7px;border-radius:16px;background:rgba(0,0,0,.62);
  color:#fff;font-size:12px;line-height:1}
.mf-rail .mf-act:hover{background:rgba(0,0,0,.82);color:#fff}
.mf-rail .mf-act svg{font-size:26px}
.mf-rail .mf-like.is-on{color:#ff5d7d}
.mf-rail .mf-reshare.is-on{color:#3ddc97}

/* The same rail, shown OUTSIDE the cards (inside the collapsed Party rail of Feed.js).
   It follows whichever post is centered; colours come from the page theme. */
.mf-rail-out{display:flex;flex-direction:column;align-items:center;gap:10px;width:100%;margin-top:2px;padding-top:14px;
  border-top:1px solid var(--mf-border);animation:mf-rise .22s ease-out both}
.mf-rail-out .mf-act{flex-direction:column;gap:2px;width:52px;height:auto;padding:9px 0 7px;border-radius:16px;background:var(--mf-surface-2);
  color:var(--mf-text);font-size:12px;line-height:1}
.mf-rail-out .mf-act:hover{background:var(--mf-surface-3)}
.mf-rail-out .mf-act svg{font-size:26px}
.mf-rail-out .mf-like.is-on{color:#ff5d7d}
.mf-rail-out .mf-reshare.is-on{color:#3ddc97}
.mf-stage.rail-out .mf-cap{right:10px}

/* ---------- Shop deck (under the comments button) ----------
   Small rounded-square product images stacked one under another; the ones
   behind peek out a little to the right. Swipe the top one (left or right) to
   reveal the next, tap it to open that product's link. After the last one, a
   website icon takes its place. */
.mf-shop{display:flex;flex-direction:column;align-items:center;gap:3px;width:56px;padding-top:2px}
.mf-deck{position:relative;width:56px;height:52px;touch-action:pan-y;user-select:none;-webkit-user-select:none;-webkit-touch-callout:none}
.mf-deck-card{position:absolute;top:4px;left:0;width:44px;height:44px;padding:0;border-radius:13px;overflow:hidden;display:grid;place-items:center;
  background:#f4f5f8;color:#1b2030;border:2px solid #fff;box-shadow:0 4px 12px rgba(0,0,0,.45);cursor:pointer;text-decoration:none;
  transform:translateX(calc(var(--k,0) * 4px)) scale(calc(1 - var(--k,0) * .08));transform-origin:center;
  transition:transform .3s cubic-bezier(.3,.9,.3,1),opacity .22s ease;will-change:transform;-webkit-tap-highlight-color:transparent}
.mf-deck-card img{display:block;width:100%;height:100%;object-fit:cover;pointer-events:none;-webkit-user-drag:none}
.mf-deck-card svg{font-size:22px}
.mf-deck-card:focus-visible{outline:2px solid var(--mf-accent);outline-offset:2px}
.mf-deck-web{background:var(--mf-grad);color:#fff}
.mf-deck-web svg{font-size:26px}
.mf-deck-label{font-size:10.5px;font-weight:800;line-height:1;letter-spacing:.02em;color:#fff;background:rgba(0,0,0,.62);padding:3px 7px;
  border-radius:999px;white-space:nowrap}

/* Caption over the content (only while the card is centered) */
.mf-cap{position:absolute;z-index:6;left:10px;right:78px;bottom:var(--mf-stack);max-height:42%;
  overflow:auto;padding:10px 12px;border-radius:14px;background:rgba(0,0,0,.62);color:#fff;font-size:14.5px;line-height:1.45;
  white-space:pre-wrap;overflow-wrap:anywhere;cursor:pointer;animation:mf-rise .22s ease-out both}
.mf-cap.is-clamped{overflow:hidden;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical}

@keyframes mf-rise{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
@keyframes mf-fade{from{opacity:0}to{opacity:1}}
@keyframes mf-sheet{from{transform:translateY(32px);opacity:0}to{transform:none;opacity:1}}

/* ---------- Carousel: rounded cards side by side, the next one peeks in ---------- */
.mf-car{position:absolute;inset:0;background:transparent}
.mf-car:focus-visible{outline:2px solid var(--mf-accent);outline-offset:-2px;border-radius:var(--mf-slide-r)}
.mf-car-track{position:relative;display:flex;gap:10px;width:100%;height:100%;overflow-x:auto;overflow-y:hidden;
  scroll-snap-type:x mandatory;scrollbar-width:none;-ms-overflow-style:none;overscroll-behavior-x:contain;-webkit-overflow-scrolling:touch;cursor:grab}
.mf-car-track::-webkit-scrollbar{display:none}
.mf-car-track.is-single{overflow:hidden;cursor:default}
.mf-car-track.is-dragging{scroll-snap-type:none;scroll-behavior:auto;cursor:grabbing;user-select:none}
.mf-car-slide{position:relative;isolation:isolate;flex:0 0 var(--mf-slide-w,84%);min-width:0;height:100%;border-radius:var(--mf-slide-r);overflow:hidden;
  background:var(--mf-surface-2);scroll-snap-align:start;scroll-snap-stop:always;display:flex;align-items:center;justify-content:center}
.mf-car-track.is-single .mf-car-slide{flex-basis:100%}
.mf-car-slide--text{padding:64px 18px calc(var(--mf-stack) + 8px)}
.mf-car-slide--music{padding:64px 12px calc(var(--mf-stack) + 8px)}
.mf-car-img{display:block;width:100%;height:100%;object-fit:var(--mf-media-fit,cover);user-select:none;-webkit-user-drag:none}
.mf-car-music{width:100%;display:flex;justify-content:center}
.mf-car-music>*{width:min(100%,460px)}
.mf-car-text{margin:0;padding:10px 8px;width:100%;max-height:100%;overflow:auto;text-align:center;font-size:clamp(20px,3vw,32px);
  line-height:1.45;font-weight:700;white-space:pre-wrap;overflow-wrap:anywhere;color:var(--mf-text)}

/* Dots: centered at the bottom of the stage, just above the author / credits bar */
.mf-car-dots{position:absolute;z-index:6;left:0;right:0;bottom:calc(var(--mf-fb) + var(--mf-bar-h) + 12px);display:flex;justify-content:center;pointer-events:none}
.mf-car-dots-in{display:inline-flex;align-items:center;padding:3px 7px;border-radius:999px;background:rgba(0,0,0,.5);pointer-events:auto;
  max-width:calc(100% - 24px);overflow:hidden}
.mf-car-dot{height:18px;padding:0 3px;border:0;background:transparent;cursor:pointer;display:grid;place-items:center}
.mf-car-dot::after{content:'';display:block;width:6px;height:6px;border-radius:999px;background:rgba(255,255,255,.5);transition:width .2s,background-color .2s}
.mf-car-dot.is-active::after{width:16px;background:#fff}

.mf-car-btn{position:absolute;top:50%;transform:translateY(-50%);z-index:2;width:36px;height:36px;border-radius:50%;border:0;background:rgba(0,0,0,.62);
  color:#fff;display:grid;place-items:center;cursor:pointer;opacity:0;transition:opacity .15s,background-color .15s}
.mf-car-btn:hover{background:rgba(0,0,0,.8)}
.mf-car-btn--prev{left:10px}
.mf-car-btn--next{right:10px}
.mf-car-btn:disabled{display:none}
@media (hover:hover){.mf-car:hover .mf-car-btn,.mf-car:focus-within .mf-car-btn{opacity:1}}
@media (hover:none){.mf-car-btn{display:none}}

/* ---------- Video (our VideoPlayer, driven in three steps) ----------
   preview  -> muted autoplay, only the picture is shown, a mute / unmute icon
   detail   -> after the 1st tap: plays with sound, still no controls
   controls -> after the next tap: paused, the player's own controls appear */
.mf-vid{position:relative;display:block;width:100%;height:100%;background:#000}
.mf-vid-player{position:absolute;inset:0;display:flex}
.mf-vid-player>*{flex:1;min-width:0;width:100%;height:100%}
.mf-vid-player video{display:block;width:100%;height:100%;object-fit:var(--mf-media-fit,cover);background:#000}
.mf-vid:not(.is-controls) .mf-vid-player *{visibility:hidden}
.mf-vid:not(.is-controls) .mf-vid-player video{visibility:visible}
.mf-vid:not(.is-controls) .mf-vid-player video::-webkit-media-controls,
.mf-vid:not(.is-controls) .mf-vid-player video::-webkit-media-controls-enclosure{display:none!important}
.mf-vid-tap{position:absolute;inset:0;z-index:2;cursor:pointer;outline:none;-webkit-tap-highlight-color:transparent}
.mf-vid-tap:focus-visible{outline:2px solid var(--mf-accent);outline-offset:-2px;border-radius:var(--mf-slide-r)}
.mf-vid-mute{position:absolute;z-index:3;top:56px;left:12px;width:40px;height:40px;border-radius:50%;border:0;background:rgba(0,0,0,.6);color:#fff;
  display:grid;place-items:center;cursor:pointer;animation:mf-fade .2s ease-out both;transition:background-color .15s}
.mf-vid-mute:hover{background:rgba(0,0,0,.82)}
.mf-vid-mute svg{font-size:22px}

/* ---------- Comments panel (slides up inside the card) ---------- */
.mf-cm-back{position:absolute;inset:0;z-index:8;background:rgba(0,0,0,.35);animation:mf-fade .18s ease-out both}
.mf-cm{position:absolute;z-index:9;left:0;right:0;bottom:0;max-height:78%;display:flex;flex-direction:column;background:var(--mf-surface);
  color:var(--mf-text);border-top:1px solid var(--mf-border-strong);border-radius:20px 20px 0 0;box-shadow:0 -12px 40px rgba(0,0,0,.4);
  padding:6px 0 calc(var(--mf-fb) + 8px);animation:mf-sheet .22s ease-out both}
.mf-cm-head{display:flex;align-items:center;justify-content:space-between;padding:6px 8px 4px 16px}
.mf-cm-head h4{margin:0;font-size:15px;font-weight:800}
.mf-cm-head h4 span{margin-left:6px;color:var(--mf-faint);font-weight:700}
.mf-cm-list{flex:1;min-height:60px;overflow-y:auto;overscroll-behavior:contain;padding:6px 16px 10px;display:flex;flex-direction:column;gap:10px}
.mf-cm-empty{margin:18px 0;text-align:center;color:var(--mf-faint);font-size:14px}
.mf-cm .mf-comment-form{padding:6px 16px 0}
.mf-cm .mf-login-hint{padding:6px 16px 0}
.mf-comment{display:flex;gap:10px;align-items:flex-start}
.mf-comment-bubble{background:var(--mf-surface-2);border-radius:4px 16px 16px 16px;padding:8px 12px;min-width:0}
.mf-comment-name{font-weight:700;font-size:13.5px;color:var(--mf-text)}
.mf-comment-time{font-size:11.5px;color:var(--mf-faint);margin-left:8px;font-weight:500}
.mf-comment-text{font-size:14.5px;line-height:1.45;overflow-wrap:anywhere;color:var(--mf-text)}
.mf-comment-form{display:flex;align-items:center;gap:10px}
.mf-send{flex:none;width:42px;height:42px;border-radius:50%;border:0;background:var(--mf-grad);color:#fff;display:grid;place-items:center;cursor:pointer;
  transition:filter .15s,opacity .15s}
.mf-send:hover{filter:brightness(1.08)}
.mf-send:disabled{opacity:.35;cursor:not-allowed;filter:grayscale(.4)}
.mf-send svg{font-size:20px}
.mf-login-hint{margin:0;font-size:13.5px;color:var(--mf-faint)}

/* ---------- Reshare dialog ---------- */
.mf-modal{position:fixed;inset:0;z-index:1000;display:flex;align-items:center;justify-content:center;padding:16px;background:var(--mf-overlay)}
.mf-modal-card{width:100%;max-width:480px;background:var(--mf-surface);border:1px solid var(--mf-border-strong);border-radius:20px;
  box-shadow:0 24px 60px rgba(0,0,0,.45);padding:18px;display:flex;flex-direction:column;gap:14px;color:var(--mf-text)}
.mf-modal-head{display:flex;align-items:center;justify-content:space-between}
.mf-modal-head h4{margin:0;font-size:17px;font-weight:800}
.mf-quote{display:flex;gap:10px;padding:12px;border-radius:14px;background:var(--mf-surface-2);border:1px solid var(--mf-border)}
.mf-quote strong{display:block;font-size:14px}
.mf-quote p{margin:2px 0 0;font-size:14px;color:var(--mf-muted);display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden;overflow-wrap:anywhere}
.mf-modal-actions{display:flex;justify-content:flex-end;gap:10px}
.mf-modal-x{width:36px;height:36px;border-radius:10px;border:0;background:transparent;color:var(--mf-muted);display:grid;place-items:center;cursor:pointer}
.mf-modal-x:hover{background:var(--mf-surface-2);color:var(--mf-text)}

/* ---------- Narrow cards: hide the long labels, keep the icons ---------- */
@container mfstage (max-width:419px){
  .mf-badge-t,.mf-credits small{display:none}
}
@container mfstage (max-width:319px){
  .mf-co-t{display:none}
}
@media (max-width:480px){
  .mf-badge-t,.mf-credits small{display:none}
}
@media (max-width:425px){
  .mf-co-t{display:none}
}
@media (max-width:640px){
  .mf-post{--mf-post-r:24px;--mf-slide-r:18px;--mf-frame:8px}
  .mf-modal{align-items:flex-end;padding:0}
  .mf-modal-card{max-width:none;border-radius:20px 20px 0 0;padding-bottom:max(18px,env(safe-area-inset-bottom))}
}
@media (max-width:767px){
  /* Feed.js squares the outer edge of posts on phones; keep the framed, rounded look */
  .mf-feed .mf-main .mf-post{border-width:1px;border-radius:var(--mf-post-r);box-shadow:none}
}
/* short screens: tighten the rail so the shop deck still fits */
@media (max-height:700px){
  .mf-rail,.mf-rail-out{gap:6px}
  .mf-rail .mf-act,.mf-rail-out .mf-act{padding:6px 0 4px}
  .mf-rail .mf-act svg,.mf-rail-out .mf-act svg{font-size:22px}
}
@media (max-height:600px){
  .mf-deck-label{display:none}
}
@media (prefers-reduced-motion:reduce){
  .mf-like.is-pop svg,.mf-rail,.mf-rail-out,.mf-cap,.mf-co,.mf-cm,.mf-cm-back,.mf-vid-mute{animation:none!important}
  .mf-deck-card{transition:none!important}
}
`;

export function injectStyles(id, css) {
  if (typeof document === 'undefined') return;
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('style');
    el.id = id;
    document.head.appendChild(el);
  }
  if (el.textContent !== css) el.textContent = css;
}
injectStyles('mf-post-css', POST_CSS);

/* ------------------------------------------------------------------ */
/* Theme hook (shared by Feed + CreatePost)                            */
/* ------------------------------------------------------------------ */
export const THEME_KEY = 'myfavv-theme'; // 'dark' | 'light'

function readStoredTheme() {
  try {
    const t = localStorage.getItem(THEME_KEY);
    if (t === 'light' || t === 'dark') return t;
  } catch {}
  return 'dark'; // default
}

export function useMfTheme() {
  const [theme, setTheme] = useState(readStoredTheme);
  const toggle = useCallback(() => setTheme((t) => (t === 'dark' ? 'light' : 'dark')), []);

  useEffect(() => {
    try { localStorage.setItem(THEME_KEY, theme); } catch {}

    const root = document.documentElement;
    const prevScheme = root.style.colorScheme;
    const prevBodyBg = document.body.style.backgroundColor;
    root.style.colorScheme = theme;
    document.body.style.backgroundColor = theme === 'dark' ? '#0b0d12' : '#f3f4f8';

    // colors the browser UI on phones
    let meta = document.querySelector('meta[name="theme-color"]');
    const hadMeta = !!meta;
    const prevMeta = meta ? meta.getAttribute('content') : null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'theme-color');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', theme === 'dark' ? '#0f1218' : '#ffffff');

    return () => {
      root.style.colorScheme = prevScheme;
      document.body.style.backgroundColor = prevBodyBg;
      if (hadMeta) meta.setAttribute('content', prevMeta || '');
      else meta.remove();
    };
  }, [theme]);

  return [theme, toggle];
}

/* ------------------------------------------------------------------ */
/* Post background colors                                              */
/* ------------------------------------------------------------------ */
// '' = theme default. Stored on the post as `bgColor` (e.g. "#581c87").
export const POST_COLORS = [
  { name: 'Default',  value: '' },
  { name: 'Midnight', value: '#0f172a' },
  { name: 'Indigo',   value: '#312e81' },
  { name: 'Violet',   value: '#581c87' },
  { name: 'Berry',    value: '#831843' },
  { name: 'Crimson',  value: '#7f1d1d' },
  { name: 'Rust',     value: '#7c2d12' },
  { name: 'Forest',   value: '#14532d' },
  { name: 'Teal',     value: '#134e4a' },
  { name: 'Ocean',    value: '#0c4a6e' },
  { name: 'Slate',    value: '#334155' },
  { name: 'Sand',     value: '#fef3c7' },
  { name: 'Mint',     value: '#dcfce7' },
  { name: 'Sky',      value: '#dbeafe' },
  { name: 'Blush',    value: '#fce7f3' },
  { name: 'Lavender', value: '#ede9fe' },
  { name: 'Peach',    value: '#ffedd5' },
];

const HEX_RE = /^#[0-9a-f]{6}$/i;
export const isHexColor = (v) => typeof v === 'string' && HEX_RE.test(v.trim());

function hexToRgb(hex) {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function rgbToHex(rgb) {
  return '#' + rgb
    .map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0'))
    .join('');
}
function mixRgb(a, b, t) {
  return a.map((v, i) => v + (b[i] - v) * t);
}
function relLuminance([r, g, b]) {
  const f = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

// CSS variable overrides so everything inside a card stays readable on any chosen color
export function cardColorVars(color) {
  if (!isHexColor(color)) return undefined;
  const base = hexToRgb(color.trim());
  const darkBg = relLuminance(base) < 0.179;
  const text = darkBg ? [244, 246, 251] : [20, 24, 38];
  const toward = darkBg ? [255, 255, 255] : [0, 0, 0];

  return {
    '--mf-surface': rgbToHex(base),
    '--mf-surface-2': rgbToHex(mixRgb(base, toward, 0.10)),
    '--mf-surface-3': rgbToHex(mixRgb(base, toward, 0.18)),
    '--mf-border': rgbToHex(mixRgb(base, toward, 0.16)),
    '--mf-border-strong': rgbToHex(mixRgb(base, toward, 0.28)),
    '--mf-text': rgbToHex(text),
    '--mf-muted': rgbToHex(mixRgb(text, base, 0.30)),
    '--mf-faint': rgbToHex(mixRgb(text, base, 0.50)),
    colorScheme: darkBg ? 'dark' : 'light',
  };
}

/* ------------------------------------------------------------------ */
/* Media items: one post can hold several images / videos / audio /     */
/* text slides. `mediaItems` is the source of truth; old single-media   */
/* posts (just `media` + `mediaType`) keep working.                     */
/* ------------------------------------------------------------------ */
const VIDEO_EXT = /\.(mp4|webm|mov|avi|mkv)$/i;
const AUDIO_EXT = /\.(mp3|wav|ogg|aac|flac|m4a)$/i;

function typeFromName(name) {
  const n = String(name || '');
  if (VIDEO_EXT.test(n)) return 'video';
  if (AUDIO_EXT.test(n)) return 'music';
  return 'image';
}

// -> [{ type: 'image'|'video'|'music', name } | { type: 'text', text }]
export function getMediaItems(post) {
  if (!post) return [];

  let raw = post.mediaItems;
  if (typeof raw === 'string') {
    try { raw = JSON.parse(raw); } catch { raw = null; }
  }

  if (Array.isArray(raw) && raw.length) {
    const out = [];
    for (const it of raw) {
      if (!it || typeof it !== 'object') continue;
      let type = String(it.type || '').toLowerCase();
      if (type === 'audio') type = 'music';
      if (type === 'text') {
        if (it.text) out.push({ type: 'text', text: String(it.text) });
        continue;
      }
      const name = firstFile(it.name);
      if (!name || /[{}]/.test(name)) continue;
      if (!['image', 'video', 'music'].includes(type)) type = typeFromName(name);
      out.push({ type, name: String(name) });
    }
    return out;
  }

  // legacy single-media post
  const name = firstFile(post.media);
  if (name && !/[{}]/.test(name)) {
    const mt = String(post.mediaType || '').toLowerCase();
    let type = typeFromName(name);
    if (mt === 'video' || mt === 'image') type = mt;
    else if (mt === 'music' || mt === 'audio') type = 'music';
    return [{ type, name: String(name) }];
  }
  return [];
}

/* ------------------------------------------------------------------ */
/* Shop: up to 4 products / services + the brand website, attached to   */
/* a post. Stored on the post as  products (JSON)  and  brandWebsite.   */
/* Product images live in R2 under  social-media-assets/products/.      */
/* ------------------------------------------------------------------ */
export const MAX_PRODUCTS = 4;
export const PRODUCTS_FOLDER = 'products';
const MEDIA_CDN = 'https://media.myfavv.live';

export function productImageUrl(name) {
  const n = String(name || '').trim();
  return n ? `${MEDIA_CDN}/${PRODUCTS_FOLDER}/${encodeURIComponent(n)}` : '';
}

// Returns a safe absolute http(s) URL, or null. "shop.com/x" becomes "https://shop.com/x";
// javascript:, data: and other schemes are rejected, so a link can never run code.
export function normalizeUrl(value) {
  let v = String(value == null ? '' : value).trim();
  if (!v) return null;
  if (/^(?!https?:)[a-z][a-z0-9+.-]*:\/\//i.test(v)) return null;
  if (/^(javascript|data|vbscript|file|blob|about|mailto|tel|sms):/i.test(v)) return null;
  if (!/^https?:\/\//i.test(v)) v = `https://${v.replace(/^\/+/, '')}`;
  try {
    const u = new URL(v);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    if (!u.hostname || (!u.hostname.includes('.') && u.hostname !== 'localhost')) return null;
    return u.href;
  } catch {
    return null;
  }
}

// -> [{ image, url, title }]  (invalid rows are dropped, never more than MAX_PRODUCTS)
export function getProducts(post) {
  let raw = post && post.products;
  if (typeof raw === 'string') {
    try { raw = JSON.parse(raw); } catch { raw = null; }
  }
  if (!Array.isArray(raw)) return [];
  const out = [];
  for (const p of raw) {
    if (!p || typeof p !== 'object') continue;
    const image = String(firstFile(p.image) || '').trim();
    const url = normalizeUrl(p.url);
    if (!image || /[{}/\\]/.test(image) || !url) continue;
    out.push({ image, url, title: String(p.title || '').trim().slice(0, 80) });
    if (out.length >= MAX_PRODUCTS) break;
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

// AES decrypt
function decryptAESGCM(base64Key, base64Data) {
  const keyBytes = Uint8Array.from(atob(base64Key), c => c.charCodeAt(0));
  const dataBytes = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
  const iv = dataBytes.slice(0, 12);
  const ct = dataBytes.slice(12);
  return window.crypto.subtle
    .importKey('raw', keyBytes, 'AES-GCM', true, ['decrypt'])
    .then(k => window.crypto.subtle.decrypt({ name: 'AES-GCM', iv }, k, ct))
    .then(buf => new TextDecoder().decode(buf));
}

// sounds
const likeSound = new Audio("/sounds/like.mp3");
const reshareSound = new Audio("/sounds/reshare.mp3");

// Keeps links from showing an underline even outside the Feed page
const noUnderline = { textDecoration: 'none' };

// play() returns a promise that rejects when the browser blocks it; never let that bubble up
function safePlay(v) {
  try {
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  } catch {}
}

// PocketBase subscribe() returns a Promise of an unsubscribe function in newer SDKs
function safeUnsub(u) {
  try {
    if (u && u.then) u.then(f => f && f()).catch(() => {});
    else if (u) u();
  } catch {}
}

// Same as pb.collection(name).subscribe('*', ...) but never throws / rejects loudly
function subscribeSafe(name, handler) {
  try {
    const p = pb.collection(name).subscribe('*', handler);
    if (p && p.catch) p.catch(() => {});
    return p;
  } catch {
    return null;
  }
}

// Show the user's real name; fall back to username, then a placeholder
function getDisplayName(u) {
  return (u?.name && u.name.trim()) || u?.username || 'Anon';
}

// PocketBase dates look like "2026-10-11 22:43:57.538Z" (a space, which Safari can't parse)
function parsePbDate(v) {
  if (!v) return null;
  const d = new Date(String(v).replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? null : d;
}

function timeAgo(v) {
  const d = parsePbDate(v);
  if (!d) return '';
  const s = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 45) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: d.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  });
}

// 1234 -> "1.2K"
const compactFmt = (() => {
  try { return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }); } catch { return null; }
})();
function formatCount(n) {
  const v = Number(n) || 0;
  if (!compactFmt || v < 1000) return String(v);
  return compactFmt.format(v);
}

// Link only when we actually know the profile id
function MaybeLink({ to, children, ...props }) {
  return to ? <Link to={to} {...props}>{children}</Link> : <span {...props}>{children}</span>;
}

// Avatar image from PocketBase, with an initial-letter fallback (also used by Feed.js / CreatePost.js)
export function Avatar({ user, size = 40 }) {
  const [failed, setFailed] = useState(false);

  useEffect(() => { setFailed(false); }, [user?.id, user?.avatar]);

  const file = firstFile(user?.avatar);
  let url = '';
  try { url = file && !failed ? pb.files.getURL(user, file) : ''; } catch { url = ''; }

  return (
    <span
      className="mf-avatar"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.42) }}
      aria-hidden="true"
    >
      {url
        ? <img src={url} alt="" loading="lazy" onError={() => setFailed(true)} />
        : <span>{getDisplayName(user)[0]?.toUpperCase() || '?'}</span>}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Action rail store                                                   */
/* Normally every card draws its own like / reshare / comment / shop   */
/* rail inside the card. Feed.js can switch the rail "outside" (when   */
/* its Party column is collapsed): cards then stop drawing it and the  */
/* centered card publishes its rail data here instead, which <PostRail>*/
/* (rendered by Feed.js inside the collapsed Party rail) displays.     */
/* ------------------------------------------------------------------ */
const railStore = (() => {
  let outside = false;
  let data = null;
  const subs = new Set();
  const emit = () => subs.forEach((fn) => fn());
  return {
    getOutside: () => outside,
    getData: () => data,
    setOutside(v) {
      const next = !!v;
      if (outside === next) return;
      outside = next;
      if (!next) data = null;
      emit();
    },
    publish(d) { data = d; emit(); },
    clear(id) { if (data && data.id === id) { data = null; emit(); } },
    subscribe(fn) { subs.add(fn); return () => subs.delete(fn); },
  };
})();

export function setRailOutside(value) { railStore.setOutside(value); }

function useRailOutside() {
  const [value, setValue] = useState(railStore.getOutside());
  useEffect(() => {
    setValue(railStore.getOutside());
    return railStore.subscribe(() => setValue(railStore.getOutside()));
  }, []);
  return value;
}

function useRailData() {
  const [value, setValue] = useState(railStore.getData());
  useEffect(() => {
    setValue(railStore.getData());
    return railStore.subscribe(() => setValue(railStore.getData()));
  }, []);
  return value;
}

/* ------------------------------------------------------------------ */
/* "Which card is in the middle of the screen?"                        */
/* One shared observer watches a thin band across the middle of the    */
/* viewport. Exactly one card is "active" at a time; while the band    */
/* crosses the gap between two cards, the previous one stays active.   */
/* ------------------------------------------------------------------ */
const activeCards = (() => {
  const supported = typeof window !== 'undefined' && typeof window.IntersectionObserver !== 'undefined';
  const idByEl = new Map();
  const inBand = new Set();
  const listeners = new Set();
  let observer = null;
  let currentId = null;

  const pick = () => {
    if (inBand.size === 0) return;
    const mid = window.innerHeight / 2;
    let bestId = null;
    let bestDist = Infinity;
    inBand.forEach((el) => {
      const r = el.getBoundingClientRect();
      const d = Math.abs((r.top + r.bottom) / 2 - mid);
      if (d < bestDist) { bestDist = d; bestId = idByEl.get(el); }
    });
    if (bestId != null && bestId !== currentId) {
      currentId = bestId;
      listeners.forEach((fn) => fn(currentId));
    }
  };

  const ensureObserver = () => {
    if (observer || !supported) return;
    observer = new window.IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) inBand.add(e.target);
        else inBand.delete(e.target);
      }
      pick();
    }, { rootMargin: '-49% 0px -49% 0px', threshold: 0 });
  };

  return {
    supported,
    get current() { return currentId; },
    register(el, id) {
      if (!supported) return;
      if (idByEl.size === 0) currentId = null; // fresh start (e.g. coming back to the feed)
      idByEl.set(el, id);
      ensureObserver();
      observer.observe(el);
    },
    unregister(el) {
      idByEl.delete(el);
      inBand.delete(el);
      if (observer) {
        observer.unobserve(el);
        if (idByEl.size === 0) { observer.disconnect(); observer = null; }
      }
    },
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
  };
})();

function useActiveCard(ref, id) {
  // browsers without IntersectionObserver simply show every card's details
  const [active, setActive] = useState(!activeCards.supported);

  useEffect(() => {
    if (!activeCards.supported) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    activeCards.register(el, id);
    setActive(activeCards.current === id);
    const off = activeCards.subscribe((cur) => setActive(cur === id));
    return () => { off(); activeCards.unregister(el); };
  }, [ref, id]);

  return active;
}

/* ------------------------------------------------------------------ */
/* View counting                                                       */
/* A "view" = a logged-in user (not the post's creator) keeps the card */
/* centered for 1.5 s. One view per user per post (post_views).        */
/* ------------------------------------------------------------------ */
const VIEW_DWELL_MS = 1500;
const viewedPosts = new Set();   // "userId:postId" already counted in this session
let viewsDisabled = false;       // set if the post_views collection doesn't exist

/* ------------------------------------------------------------------ */
/* Video slide                                                         */
/* Uses our existing VideoPlayer. We never replace it; we only drive   */
/* the <video> element it renders:                                     */
/*   preview  : muted autoplay (+ mute / unmute icon)                  */
/*   detail   : 1st tap -> plays with sound                            */
/*   controls : next tap -> paused, the player's controls are shown    */
/* ------------------------------------------------------------------ */
function VideoSlide({ url, poster, playing, onErr }) {
  const wrapRef = useRef(null);
  const nativeControls = useRef(false);   // did the player render the video with native controls?
  const [vid, setVid] = useState(null);
  const [mode, setMode] = useState('preview');   // 'preview' | 'detail' | 'controls'
  const [soundOn, setSoundOn] = useState(false); // sound toggle while in preview

  // find the <video> that VideoPlayer renders (and notice if it is re-created)
  useEffect(() => {
    const root = wrapRef.current;
    if (!root) return undefined;
    let current = null;
    const find = () => {
      if (current && root.contains(current)) return;
      current = root.querySelector('video');
      if (current) nativeControls.current = !!current.controls;
      setVid(current);
    };
    find();
    const mo = new MutationObserver(find);
    mo.observe(root, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, [url]);

  // phones: play inline, allow muted autoplay
  useEffect(() => {
    if (!vid) return;
    try {
      vid.playsInline = true;
      vid.setAttribute('playsinline', '');
      vid.setAttribute('webkit-playsinline', '');
      vid.defaultMuted = true;
      if (vid.preload === 'none') vid.preload = 'metadata';
    } catch {}
  }, [vid]);

  // slides that are not playing (e.g. the neighbour peeking in) show a first frame
  useEffect(() => {
    if (!vid || playing) return undefined;
    const nudge = () => { try { if (!vid.currentTime) vid.currentTime = 0.05; } catch {} };
    if (vid.readyState >= 1) nudge();
    else vid.addEventListener('loadedmetadata', nudge, { once: true });
    return () => vid.removeEventListener('loadedmetadata', nudge);
  }, [vid, playing]);

  // off screen (or another slide): pause and go back to the muted preview
  useEffect(() => {
    if (!vid || playing) return;
    try { vid.pause(); vid.muted = true; vid.controls = false; } catch {}
    setMode('preview');
    setSoundOn(false);
  }, [vid, playing]);

  // apply the current mode while this slide is the one playing
  useEffect(() => {
    if (!vid || !playing) return;
    try {
      if (mode === 'preview') {
        vid.muted = !soundOn;
        vid.controls = false;
        vid.loop = true;
        safePlay(vid);
      } else if (mode === 'detail') {
        vid.muted = false;
        vid.controls = false;
        vid.loop = false;
        safePlay(vid);
      } else {
        vid.loop = false;
        vid.pause();
        // if the player draws its own controls use those, otherwise fall back to the browser's
        const own = wrapRef.current && wrapRef.current.querySelector(
          '.mf-vid-player button, .mf-vid-player input[type="range"], .mf-vid-player [role="slider"]'
        );
        vid.controls = nativeControls.current || !own;
      }
    } catch {}
  }, [vid, playing, mode, soundOn]);

  const toggleSound = (e) => {
    e.stopPropagation();
    const next = !soundOn;
    setSoundOn(next);
    if (vid) { try { vid.muted = !next; } catch {} }
  };

  const onTap = () => {
    if (mode === 'preview') {
      setMode('detail');
      if (vid) {
        try { vid.muted = false; vid.loop = false; } catch {}
        safePlay(vid);
      }
    } else if (mode === 'detail') {
      setMode('controls');
      if (vid) { try { vid.pause(); } catch {} }
    }
  };

  return (
    <div className={`mf-vid is-${mode}`} ref={wrapRef} onErrorCapture={onErr}>
      <div className="mf-vid-player">
        <VideoPlayer key={url} src={url} poster={poster} />
      </div>

      {mode !== 'controls' && (
        <div
          className="mf-vid-tap"
          role="button"
          tabIndex={0}
          aria-label={mode === 'preview' ? 'Play video with sound' : 'Pause video and show controls'}
          onClick={onTap}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTap(); }
          }}
        />
      )}

      {playing && mode === 'preview' && (
        <button
          type="button"
          className="mf-vid-mute"
          onClick={toggleSound}
          aria-label={soundOn ? 'Mute video' : 'Unmute video'}
          title={soundOn ? 'Mute' : 'Unmute'}
        >
          {soundOn ? <VolumeUpRoundedIcon /> : <VolumeOffRoundedIcon />}
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Carousel                                                            */
/* ------------------------------------------------------------------ */

// One slide. Tries the CDN first, then falls back to the PocketBase URL once.
function SlideContent({ post, item, index, posterUrl, authorName, playing }) {
  const [fallback, setFallback] = useState(false);

  useEffect(() => { setFallback(false); }, [item.name]);

  if (item.type === 'text') {
    return <p className="mf-car-text">{item.text}</p>;
  }

  const url = fallback ? getPbUrl(post, item.name) : getMediaUrl(post, item.name);
  const onErr = () => { if (!fallback) setFallback(true); };
  if (!url) return null;

  if (item.type === 'video') {
    return (
      <VideoSlide
        url={url}
        poster={index === 0 ? posterUrl : null}
        playing={playing}
        onErr={onErr}
      />
    );
  }

  if (item.type === 'music') {
    return (
      <div className="mf-car-music" onErrorCapture={onErr}>
        <MusicPlayer
          key={url}
          src={url}
          thumbnail={index === 0 && posterUrl ? posterUrl : "/default-music-cover.png"}
        />
      </div>
    );
  }

  return (
    <img
      className="mf-car-img"
      src={url}
      alt={`Post by ${authorName}, item ${index + 1}`}
      draggable={false}
      decoding="async"
      onError={onErr}
    />
  );
}

// Rounded cards side by side (the next one peeks in). Swipe on touch, drag with the
// mouse, arrows, dots and the keyboard all work.
function MediaCarousel({ post, items, authorName, posterUrl, cardActive }) {
  const count = items.length;
  const trackRef = useRef(null);
  const slideRefs = useRef([]);
  const dragRef = useRef({ down: false, started: false, startX: 0, startLeft: 0, startIdx: 0, pid: null });
  const rafRef = useRef(0);
  const activeRef = useRef(0);
  const [active, setActive] = useState(0);
  const [dragging, setDragging] = useState(false);

  activeRef.current = active;

  // which card is closest to the left edge right now
  const nearestIndex = useCallback(() => {
    const el = trackRef.current;
    if (!el || count < 2) return 0;
    const left = el.scrollLeft;
    const maxLeft = el.scrollWidth - el.clientWidth;
    if (maxLeft > 0 && left >= maxLeft - 2) return count - 1;
    let best = 0;
    let bestDist = Infinity;
    slideRefs.current.forEach((s, i) => {
      if (!s) return;
      const d = Math.abs(s.offsetLeft - left);
      if (d < bestDist) { bestDist = d; best = i; }
    });
    return best;
  }, [count]);

  const goTo = useCallback((i) => {
    const el = trackRef.current;
    if (!el) return;
    const n = Math.max(0, Math.min(count - 1, i));
    const slide = slideRefs.current[n];
    if (!slide) return;
    const maxLeft = Math.max(0, el.scrollWidth - el.clientWidth);
    el.scrollTo({ left: Math.min(slide.offsetLeft, maxLeft), behavior: 'smooth' });
  }, [count]);

  // keep the active index in range if the items change
  useEffect(() => {
    if (active > count - 1) {
      setActive(0);
      if (trackRef.current) trackRef.current.scrollLeft = 0;
    }
  }, [count, active]);

  // stop the scroll-listener's pending frame on unmount
  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);

  // pause any video/audio that slid out of view
  useEffect(() => {
    slideRefs.current.forEach((el, i) => {
      if (!el || i === active) return;
      el.querySelectorAll('video, audio').forEach((m) => { try { m.pause(); } catch {} });
    });
  }, [active]);

  // keep the same card in view when the post is resized (rotation, window resize)
  useEffect(() => {
    const el = trackRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    let lastW = el.clientWidth;
    const ro = new ResizeObserver(() => {
      if (el.clientWidth !== lastW) {
        lastW = el.clientWidth;
        const s = slideRefs.current[activeRef.current];
        if (s) el.scrollLeft = s.offsetLeft;
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const onScroll = () => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      const i = nearestIndex();
      setActive((prev) => (prev === i ? prev : i));
    });
  };

  // mouse dragging (touch uses native scrolling). The drag only starts after a few
  // pixels of movement, so a plain click still reaches the card underneath.
  const onPointerDown = (e) => {
    if (count < 2 || e.pointerType !== 'mouse' || e.button !== 0) return;
    if (e.target.closest && e.target.closest('video, audio, button, input, textarea, a, [data-nodrag]')) return;
    const el = trackRef.current;
    if (!el) return;
    dragRef.current = {
      down: true,
      started: false,
      startX: e.clientX,
      startLeft: el.scrollLeft,
      startIdx: activeRef.current,
      pid: e.pointerId,
    };
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    const el = trackRef.current;
    if (!d.down || !el) return;
    if (e.buttons === 0) { d.down = false; return; }  // button was released outside
    const dx = e.clientX - d.startX;
    if (!d.started) {
      if (Math.abs(dx) < 6) return;
      d.started = true;
      setDragging(true);
      try { el.setPointerCapture(d.pid); } catch {}
    }
    el.scrollLeft = d.startLeft - dx;
  };

  const endDrag = (e) => {
    const d = dragRef.current;
    if (!d.down) return;
    d.down = false;
    if (!d.started) return;   // it was just a click
    d.started = false;
    setDragging(false);
    const el = trackRef.current;
    if (!el) return;
    try { el.releasePointerCapture(d.pid); } catch {}
    const dx = e.type === 'pointercancel' ? 0 : e.clientX - d.startX;
    const slideW = (slideRefs.current[0] && slideRefs.current[0].offsetWidth) || el.clientWidth;
    const thr = Math.min(60, slideW * 0.12);
    let idx = nearestIndex();
    if (idx === d.startIdx) {
      if (dx < -thr) idx += 1;
      else if (dx > thr) idx -= 1;
    }
    goTo(idx);
  };

  const onKeyDown = (e) => {
    if (count < 2) return;
    if (e.target.closest && e.target.closest('video, audio, input, textarea, button, [role="button"]')) return;
    if (e.key === 'ArrowRight') { e.preventDefault(); goTo(activeRef.current + 1); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(activeRef.current - 1); }
  };

  const multi = count > 1;

  return (
    <div
      className="mf-car"
      role="region"
      aria-roledescription="carousel"
      aria-label={`${count} item${multi ? 's' : ''} in post by ${authorName}`}
      tabIndex={multi ? 0 : -1}
      onKeyDown={onKeyDown}
    >
      <div
        ref={trackRef}
        className={`mf-car-track${multi ? '' : ' is-single'}${dragging ? ' is-dragging' : ''}`}
        onScroll={onScroll}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        {items.map((it, i) => (
          <div
            key={`${i}-${it.name || 'text'}`}
            ref={(el) => { slideRefs.current[i] = el; }}
            className={`mf-car-slide mf-car-slide--${it.type}`}
            role="group"
            aria-roledescription="slide"
            aria-label={`${i + 1} of ${count}`}
            // tapping a card that is only peeking in brings it to the front instead of acting on it
            onClickCapture={(e) => {
              if (i !== activeRef.current) {
                e.preventDefault();
                e.stopPropagation();
                goTo(i);
              }
            }}
          >
            {Math.abs(i - active) <= 1 && (
              <SlideContent
                post={post}
                item={it}
                index={i}
                posterUrl={posterUrl}
                authorName={authorName}
                playing={cardActive && i === active}
              />
            )}
          </div>
        ))}
      </div>

      {multi && (
        <>
          <button
            type="button"
            className="mf-car-btn mf-car-btn--prev"
            aria-label="Previous item"
            disabled={active === 0}
            onClick={() => goTo(active - 1)}
          >
            <ChevronLeftRoundedIcon />
          </button>
          <button
            type="button"
            className="mf-car-btn mf-car-btn--next"
            aria-label="Next item"
            disabled={active === count - 1}
            onClick={() => goTo(active + 1)}
          >
            <ChevronRightRoundedIcon />
          </button>

          <div className="mf-car-dots">
            <div className="mf-car-dots-in">
              {items.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  className={`mf-car-dot${i === active ? ' is-active' : ''}`}
                  aria-label={`Go to item ${i + 1} of ${count}`}
                  aria-current={i === active ? 'true' : undefined}
                  onClick={() => goTo(i)}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Shop deck                                                           */
/* Product images stacked one under another (the ones behind peek out  */
/* to the right). Swipe the top one left or right to reveal the next,  */
/* tap it to open that product's link. When every product has been     */
/* swiped, a website icon takes its place (swipe it to start over).    */
/* ------------------------------------------------------------------ */
function ShopDeck({ products, website }) {
  const n = products.length;
  const hasSite = !!website;
  const slots = n + (hasSite ? 1 : 0);          // 0..n-1 = products, n = website
  const [index, setIndex] = useState(0);
  const [failed, setFailed] = useState({});
  const topRef = useRef(null);
  const dirs = useRef({});                       // which way each dismissed card flew off
  const drag = useRef({ down: false, moved: false, startX: 0, startY: 0, pid: null, dx: 0 });
  const suppress = useRef(false);                // a swipe must not also count as a tap

  useEffect(() => { if (index > slots - 1) setIndex(0); }, [slots, index]);

  const step = useCallback((dir) => {
    if (slots < 2) return;
    dirs.current[index] = dir;
    setIndex((cur) => (cur + 1 >= slots ? 0 : cur + 1));
  }, [slots, index]);

  const onPointerDown = (e) => {
    if (slots < 2 || (e.pointerType === 'mouse' && e.button !== 0)) return;
    drag.current = { down: true, moved: false, startX: e.clientX, startY: e.clientY, pid: e.pointerId, dx: 0 };
    suppress.current = false;
  };

  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d.down) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved) {
      if (Math.abs(dx) < 6) return;
      if (Math.abs(dy) > Math.abs(dx)) { d.down = false; return; }   // it is a vertical scroll
      d.moved = true;
      try { e.currentTarget.setPointerCapture(d.pid); } catch {}
      if (topRef.current) topRef.current.style.transition = 'none';
    }
    d.dx = dx;
    if (topRef.current) topRef.current.style.transform = `translateX(${dx}px) rotate(${dx / 12}deg)`;
  };

  const finish = (e, cancelled) => {
    const d = drag.current;
    if (!d.down) return;
    d.down = false;
    if (!d.moved) return;                         // a plain tap: the link handles it
    suppress.current = true;
    try { e.currentTarget.releasePointerCapture(d.pid); } catch {}
    const el = topRef.current;
    if (el) el.style.transition = '';
    if (!cancelled && Math.abs(d.dx) >= 34) {
      step(d.dx > 0 ? 1 : -1);
    } else if (el) {
      el.style.transform = '';                    // snap back
    }
  };

  const onCardClick = (e) => {
    if (suppress.current) { e.preventDefault(); suppress.current = false; }
  };

  const onKeyDown = (e) => {
    if (slots < 2) return;
    if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
  };

  const onWebTop = index === n;

  return (
    <div className="mf-shop">
      <div
        className="mf-deck"
        role="group"
        aria-roledescription="carousel"
        aria-label="Products and brand website"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={(e) => finish(e, false)}
        onPointerCancel={(e) => finish(e, true)}
        onKeyDown={onKeyDown}
      >
        {products.map((p, i) => {
          const k = i - index;
          const gone = k < 0;
          const dir = dirs.current[i] || -1;
          const style = gone
            ? {
                '--k': 0,
                opacity: 0,
                transform: `translateX(${dir * 46}px) rotate(${dir * 14}deg) scale(.8)`,
                pointerEvents: 'none',
                zIndex: 0,
              }
            : { '--k': Math.min(k, 3), zIndex: 10 - k };
          const label = p.title || `Product ${i + 1}`;
          return (
            <a
              key={`${p.image}-${i}`}
              ref={k === 0 ? topRef : null}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mf-deck-card"
              style={style}
              draggable={false}
              tabIndex={k === 0 ? 0 : -1}
              aria-hidden={k !== 0}
              aria-label={`Open ${label}`}
              title={label}
              onClick={onCardClick}
            >
              {failed[p.image]
                ? <ShoppingBagRoundedIcon />
                : (
                  <img
                    src={productImageUrl(p.image)}
                    alt=""
                    draggable={false}
                    decoding="async"
                    onError={() => setFailed((f) => ({ ...f, [p.image]: true }))}
                  />
                )}
            </a>
          );
        })}

        {hasSite && (
          <a
            ref={onWebTop ? topRef : null}
            href={website}
            target="_blank"
            rel="noopener noreferrer"
            className="mf-deck-card mf-deck-web"
            style={onWebTop
              ? { '--k': 0, zIndex: 10 }
              : { '--k': 0, opacity: 0, transform: 'scale(.6)', pointerEvents: 'none', zIndex: 0 }}
            draggable={false}
            tabIndex={onWebTop ? 0 : -1}
            aria-hidden={!onWebTop}
            aria-label="Open the brand website"
            title="Brand website"
            onClick={onCardClick}
          >
            <LanguageRoundedIcon />
          </a>
        )}
      </div>

      <span className="mf-deck-label" aria-hidden="true">
        {index < n ? `Shop ${index + 1}/${n}` : 'Website'}
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Action rail: like / reshare / comments / shop deck                  */
/* Drawn inside the card by default, or by <PostRail> (outside).       */
/* ------------------------------------------------------------------ */
function ActionRail({ d }) {
  return (
    <>
      <button
        type="button"
        className={`mf-act mf-like${d.liked ? ' is-on' : ''}${d.likePop ? ' is-pop' : ''}`}
        onClick={d.onLike}
        aria-pressed={d.liked}
        aria-label={`${d.liked ? 'Unlike' : 'Like'} (${d.likes})`}
        title={d.liked ? 'Unlike' : 'Like'}
      >
        {d.liked ? <FavoriteRoundedIcon /> : <FavoriteBorderRoundedIcon />}
        <span>{formatCount(d.likes)}</span>
      </button>

      <button
        type="button"
        className={`mf-act mf-reshare${d.reshared ? ' is-on' : ''}`}
        onClick={d.onReshare}
        aria-pressed={d.reshared}
        aria-label={`${d.reshared ? 'Remove reshare' : 'Reshare'} (${d.reshares})`}
        title={d.reshared ? 'Remove reshare' : 'Reshare'}
      >
        <RepeatRoundedIcon />
        <span>{formatCount(d.reshares)}</span>
      </button>

      <button
        type="button"
        className="mf-act"
        onClick={d.onComments}
        aria-expanded={d.commentsOpen}
        aria-label={`Comments (${d.commentsCount})`}
        title="Comments"
      >
        <ChatBubbleOutlineRoundedIcon />
        <span>{formatCount(d.commentsCount)}</span>
      </button>

      {/* products / services attached to this post (+ the brand website) */}
      {d.hasShop && <ShopDeck key={d.id} products={d.products} website={d.website} />}
    </>
  );
}

/** Rendered by Feed.js inside its collapsed Party rail: the rail of the centered post. */
export function PostRail() {
  const d = useRailData();
  if (!d) return null;
  return (
    <div className="mf-rail-out" role="group" aria-label="Post actions">
      <ActionRail d={d} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* PostCard                                                            */
/* ------------------------------------------------------------------ */
export default function PostCard({ post, onDeleted }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const author = post.expand?.author;
  const authorId = (typeof post.author === 'string' && post.author) || author?.id || '';
  const authorName = getDisplayName(author);
  const profileHref = authorId ? `/profile/${authorId}` : null;

  const articleRef = useRef(null);
  const active = useActiveCard(articleRef, post.id);   // true while this card is in the middle of the screen
  const railOutside = useRailOutside();                // true while Feed.js shows the rail in its Party rail

  const [contentText, setContentText] = useState(post.content || '');
  const [removed, setRemoved] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const [contentOnly, setContentOnly] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [captionOpen, setCaptionOpen] = useState(false);
  const [views, setViews] = useState(0);

  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [posting, setPosting] = useState(false);

  // Likes / reshares: counts and "my record id" come from the server
  const [likes, setLikes] = useState(0);
  const [myLikeId, setMyLikeId] = useState(null);       // null = not liked
  const [reshares, setReshares] = useState(0);
  const [myReshareId, setMyReshareId] = useState(null); // null = not reshared
  const [likePop, setLikePop] = useState(false);
  const liked = !!myLikeId;
  const reshared = !!myReshareId;

  const [showReshare, setShowReshare] = useState(false);
  const [reshareText, setReshareText] = useState("");
  const [credits, setCredits] = useState(post.creatorCredits || 0);

  // busy flags block double-clicks and pause realtime syncing mid-action
  const busy = useRef({ likes: false, reshares: false });
  // sequence numbers so an older, slower response never overwrites a newer one
  const syncSeq = useRef({ likes: 0, reshares: 0 });
  const viewSeq = useRef(0);
  const viewBusy = useRef(false);

  const isPrivate = post.licenseType === 'Private';
  const isCreator = !!user?.id && authorId === user.id;
  const locked = isPrivate && !isCreator;

  const items = useMemo(() => getMediaItems(post), [post]);
  const cardVars = useMemo(() => cardColorVars(post.bgColor), [post.bgColor]);
  const posterUrl = post.thumbnail ? getMediaUrl(post, post.thumbnail) : null;
  const hasDots = !locked && items.length > 1;

  // shop: attached products / services + the brand website
  const products = useMemo(() => getProducts(post), [post]);
  const website = useMemo(() => normalizeUrl(post.brandWebsite), [post.brandWebsite]);
  const hasShop = !locked && (products.length > 0 || !!website);

  // everything except the content, the view count and the author/credits bar
  // only shows while the card is centered and "Content Only" is off
  const expanded = active && !contentOnly;

  // decrypt private captions (creator only)
  useEffect(() => {
    if (!isPrivate) {
      setContentText(post.content || post.caption || '');
      return undefined;
    }
    if (!isCreator) {
      setContentText('');
      return undefined;
    }
    if (post.encryptedContent && post.privateKey) {
      let cancelled = false;
      decryptAESGCM(post.privateKey, post.encryptedContent)
        .then(plain => { if (!cancelled) setContentText(plain); })
        .catch(() => { if (!cancelled) setContentText('[failed to decrypt]'); });
      return () => { cancelled = true; };
    }
    setContentText(post.content === '[private]' ? '' : (post.content || post.caption || ''));
    return undefined;
  }, [post, isPrivate, isCreator]);

  // close the owner menu on outside click / Escape
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const editPost = () => {
    setMenuOpen(false);
    navigate(`/create?edit=${post.id}`);
  };

  const deletePost = async () => {
    setMenuOpen(false);
    if (!window.confirm("Delete this post permanently? This can't be undone.")) return;
    try {
      await pb.collection('posts').delete(post.id, { $autoCancel: false });
      setRemoved(true);
      if (onDeleted) onDeleted(post.id);
    } catch (err) {
      console.error('Delete failed', err);
      alert('Could not delete this post. Please try again.');
    }
  };

  // comments realtime
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const list = await pb.collection('comments').getList(1, 50, {
          filter: `post="${post.id}"`,
          expand: 'author',
          sort: '-created',
          $autoCancel: false,
        });
        if (!cancelled) setComments(list.items);
      } catch {}
    };
    load();

    // { expand: 'author' } so live comments include the author's name + avatar
    const unsub = pb.collection('comments').subscribe("*", e => {
      if (e.record.post !== post.id) return;
      setComments(prev => {
        if (e.action === "create") {
          if (prev.some(x => x.id === e.record.id)) return prev;
          return [e.record, ...prev];
        }
        if (e.action === "update") {
          return prev.map(x =>
            x.id === e.record.id
              ? { ...e.record, expand: e.record.expand || x.expand }
              : x
          );
        }
        if (e.action === "delete") return prev.filter(x => x.id !== e.record.id);
        return prev;
      });
    }, { expand: 'author' });

    return () => { cancelled = true; safeUnsub(unsub); };
  }, [post.id]);

  const submitComment = async () => {
    const text = newComment.trim();
    if (!text || posting) return;
    if (!user) return alert("Login required");
    setPosting(true);
    try {
      const rec = await pb.collection("comments").create(
        { post: post.id, author: user.id, content: text },
        { $autoCancel: false }
      );
      // show it right away (the realtime event is de-duplicated by id)
      setComments(prev =>
        prev.some(x => x.id === rec.id) ? prev : [{ ...rec, expand: { author: user } }, ...prev]
      );
      setNewComment("");
    } catch (err) {
      console.error('Comment failed', err);
    } finally {
      setPosting(false);
    }
  };

  // ---------- likes & reshares: server is the source of truth ----------

  // Reads the real total + this user's own record for 'likes' or 'reshares'
  const syncCounts = useCallback(async (name) => {
    const seq = ++syncSeq.current[name];
    try {
      const [countRes, mine] = await Promise.all([
        pb.collection(name).getList(1, 1, {
          filter: `post="${post.id}"`,
          $autoCancel: false,
        }),
        user?.id
          ? pb.collection(name).getFirstListItem(
              `post="${post.id}" && author="${user.id}"`,
              { $autoCancel: false }
            ).catch(() => null)
          : Promise.resolve(null),
      ]);

      if (seq !== syncSeq.current[name]) return; // a newer sync already ran

      if (name === 'likes') {
        setLikes(countRes.totalItems);
        setMyLikeId(mine ? mine.id : null);
      } else {
        setReshares(countRes.totalItems);
        setMyReshareId(mine ? mine.id : null);
      }
    } catch (err) {
      console.error(`Failed to sync ${name}`, err);
    }
  }, [post.id, user?.id]);

  // initial load (and re-run when the user logs in / out)
  useEffect(() => {
    syncCounts('likes');
    syncCounts('reshares');
  }, [syncCounts]);

  // realtime: any change to this post's likes/reshares triggers a re-sync
  useEffect(() => {
    const handler = (name) => (e) => {
      if (e.record.post !== post.id) return;
      if (busy.current[name]) return; // our own action will re-sync when it finishes
      syncCounts(name);
    };

    const likeSub = pb.collection("likes").subscribe("*", handler('likes'));
    const reshareSub = pb.collection("reshares").subscribe("*", handler('reshares'));

    return () => { safeUnsub(likeSub); safeUnsub(reshareSub); };
  }, [post.id, syncCounts]);

  // like / unlike (one like per user per post)
  const toggleLike = async () => {
    if (!user) return alert("Login required");
    if (busy.current.likes) return;
    busy.current.likes = true;

    try {
      if (myLikeId) {
        // UNLIKE: delete my record
        const id = myLikeId;
        setMyLikeId(null);
        setLikes(n => Math.max(0, n - 1));
        await pb.collection("likes").delete(id, { $autoCancel: false });
      } else {
        // LIKE: create only if I don't already have one
        setMyLikeId('pending');
        setLikes(n => n + 1);
        setLikePop(true);
        setTimeout(() => setLikePop(false), 500);

        let rec = await pb.collection("likes").getFirstListItem(
          `post="${post.id}" && author="${user.id}"`,
          { $autoCancel: false }
        ).catch(() => null);

        if (!rec) {
          rec = await pb.collection("likes").create(
            { post: post.id, author: user.id },
            { $autoCancel: false }
          );
          likeSound.currentTime = 0;
          likeSound.play().catch(() => {});
        }
        setMyLikeId(rec.id);
      }
    } catch (err) {
      console.error('Like failed', err);
    } finally {
      busy.current.likes = false;
      syncCounts('likes'); // reconcile with the server either way
    }
  };

  // reshare button: opens the dialog, or removes my reshare if I already have one
  const onReshareClick = () => {
    if (!user) return alert("Login required");
    if (myReshareId) return undoReshare();
    setShowReshare(true);
  };

  const undoReshare = async () => {
    if (busy.current.reshares) return;
    if (!window.confirm("Remove your reshare?")) return;
    busy.current.reshares = true;

    try {
      const id = myReshareId;
      setMyReshareId(null);
      setReshares(n => Math.max(0, n - 1));
      await pb.collection("reshares").delete(id, { $autoCancel: false });
    } catch (err) {
      console.error('Undo reshare failed', err);
    } finally {
      busy.current.reshares = false;
      syncCounts('reshares');
    }
  };

  const confirmReshare = async () => {
    if (!user) return alert("Login required");
    if (busy.current.reshares) return;
    busy.current.reshares = true;

    try {
      setMyReshareId('pending');
      setReshares(n => n + 1);
      setShowReshare(false);

      // create only if I haven't already reshared this post
      let rec = await pb.collection("reshares").getFirstListItem(
        `post="${post.id}" && author="${user.id}"`,
        { $autoCancel: false }
      ).catch(() => null);

      if (!rec) {
        rec = await pb.collection("reshares").create(
          { post: post.id, author: user.id, text: reshareText },
          { $autoCancel: false }
        );
        reshareSound.currentTime = 0;
        reshareSound.play().catch(() => {});
      }
      setMyReshareId(rec.id);
      setReshareText("");
    } catch (err) {
      console.error('Reshare failed', err);
    } finally {
      busy.current.reshares = false;
      syncCounts('reshares');
    }
  };

  // Escape closes the reshare dialog
  useEffect(() => {
    if (!showReshare) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setShowReshare(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showReshare]);

  // credits
  const giveCredits = async () => {
    if (!user) return alert("Login required");
    try {
      await pb.collection("purchases").create({
        post: post.id,
        buyer: user.id,
        credits: 1,
      });
    } catch {}
  };

  // realtime credits (unchanged behaviour)
  useEffect(() => {
    const creditSub = pb.collection("purchases").subscribe("*", e => {
      if (e.record.post !== post.id) return;
      if (e.action === "create") setCredits(prev => prev + (e.record.credits || 0));
    });
    return () => safeUnsub(creditSub);
  }, [post.id]);

  // ---------- views: realtime count, never counting the creator ----------

  const syncViews = useCallback(async () => {
    if (viewsDisabled) return;
    const seq = ++viewSeq.current;
    try {
      const filter = authorId
        ? `post="${post.id}" && viewer != "${authorId}"`
        : `post="${post.id}"`;
      const res = await pb.collection('post_views').getList(1, 1, { filter, $autoCancel: false });
      if (seq !== viewSeq.current) return;
      setViews(res.totalItems);
    } catch (err) {
      if (err && err.status === 404) viewsDisabled = true; // collection not created yet
    }
  }, [post.id, authorId]);

  useEffect(() => {
    syncViews();
    const sub = subscribeSafe('post_views', (e) => {
      if (e.record.post !== post.id) return;
      syncViews();
    });
    return () => safeUnsub(sub);
  }, [post.id, syncViews]);

  const recordView = useCallback(async () => {
    if (!user?.id || viewsDisabled || viewBusy.current) return;
    const key = `${user.id}:${post.id}`;
    if (viewedPosts.has(key)) return;
    viewBusy.current = true;
    try {
      const existing = await pb.collection('post_views').getFirstListItem(
        `post="${post.id}" && viewer="${user.id}"`,
        { $autoCancel: false }
      ).catch(() => null);

      if (!existing) {
        await pb.collection('post_views').create(
          { post: post.id, viewer: user.id },
          { $autoCancel: false }
        );
      }
      viewedPosts.add(key);
      syncViews();
    } catch (err) {
      if (err && err.status === 404) viewsDisabled = true;
      else if (err && err.status === 400) viewedPosts.add(key); // already counted (unique index)
    } finally {
      viewBusy.current = false;
    }
  }, [post.id, user?.id, syncViews]);

  // count the view once the card has stayed centered for a moment
  useEffect(() => {
    if (!active || !user?.id || isCreator) return undefined;
    const t = setTimeout(recordView, VIEW_DWELL_MS);
    return () => clearTimeout(t);
  }, [active, user?.id, isCreator, recordView]);

  // ---------- overlay housekeeping ----------

  // collapsing the details (scrolled away / Content Only) also closes the panels
  useEffect(() => {
    if (!expanded) {
      setCommentsOpen(false);
      setCaptionOpen(false);
    }
  }, [expanded]);

  // Escape closes the comments panel
  useEffect(() => {
    if (!commentsOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setCommentsOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [commentsOpen]);

  // ---------- action rail: inside the card, or published to Feed.js's Party rail ----------

  // always-fresh handlers behind stable wrappers (the published rail must never call a stale closure)
  const actionsRef = useRef({});
  actionsRef.current = {
    like: toggleLike,
    reshare: onReshareClick,
    comments: () => setCommentsOpen((o) => !o),
  };
  const stableActions = useMemo(() => ({
    onLike: () => actionsRef.current.like(),
    onReshare: () => actionsRef.current.reshare(),
    onComments: () => actionsRef.current.comments(),
  }), []);

  const railData = {
    id: post.id,
    liked,
    likePop,
    likes,
    reshared,
    reshares,
    commentsCount: comments.length,
    commentsOpen,
    hasShop,
    products,
    website,
    ...stableActions,
  };

  const publishRail = railOutside && expanded;

  useEffect(() => {
    if (!publishRail) return;
    railStore.publish(railData);
  }, [publishRail, post.id, liked, likePop, likes, reshared, reshares, comments.length, commentsOpen, hasShop, products, website, stableActions]);

  useEffect(() => {
    if (!publishRail) return undefined;
    return () => railStore.clear(post.id);
  }, [publishRail, post.id]);

  const created = parsePbDate(post.created);

  if (removed) return null;

  return (
    <article
      ref={articleRef}
      className={`mf-post${active ? ' is-active' : ''}`}
      style={cardVars}
      data-post-id={post.id}
    >
      <div className={`mf-stage${hasDots ? ' has-dots' : ''}${railOutside ? ' rail-out' : ''}`}>
        {/* ---------- The content (fills the whole stage) ---------- */}
        {locked ? (
          <div className="mf-stage-center">
            <div className="mf-locked">
              <LockRoundedIcon />
              <div>
                <strong>Private content</strong>
                <span>Purchase required to view this post.</span>
              </div>
            </div>
          </div>
        ) : items.length > 0 ? (
          <MediaCarousel
            post={post}
            items={items}
            authorName={authorName}
            posterUrl={posterUrl}
            cardActive={active}
          />
        ) : (
          <div className="mf-stage-center">
            {contentText
              ? <p className="mf-stage-text">{contentText}</p>
              : <p className="mf-stage-empty">Nothing to show yet</p>}
          </div>
        )}

        {/* ---------- Top: view count (always) + Content Only toggle (when centered) ---------- */}
        <div className="mf-top">
          <span className="mf-pill mf-views" title={`${views} view${views === 1 ? '' : 's'}`} aria-label={`${views} views`}>
            <VisibilityRoundedIcon />
            <span>{formatCount(views)}</span>
          </span>

          {active && (
            <button
              type="button"
              className="mf-pill mf-co"
              onClick={() => setContentOnly(v => !v)}
              aria-pressed={contentOnly}
              title={contentOnly ? 'Show details' : 'Content only'}
            >
              {contentOnly ? <FullscreenExitRoundedIcon /> : <FullscreenRoundedIcon />}
              <span className="mf-co-t">{contentOnly ? 'Show Details' : 'Content Only'}</span>
            </button>
          )}
        </div>

        {/* ---------- Caption (when centered, for posts that also have media) ---------- */}
        {expanded && !locked && items.length > 0 && contentText && (
          <div
            className={`mf-cap${captionOpen ? '' : ' is-clamped'}`}
            role="button"
            tabIndex={0}
            title={captionOpen ? 'Tap to collapse' : 'Tap to read more'}
            onClick={() => setCaptionOpen(o => !o)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setCaptionOpen(o => !o);
              }
            }}
          >
            {contentText}
          </div>
        )}

        {/* ---------- Action rail (when centered; moves out to Feed's Party rail when it is collapsed) ---------- */}
        {expanded && !railOutside && (
          <div className="mf-rail">
            <ActionRail d={railData} />
          </div>
        )}

        {/* ---------- Bottom bar (always): author on the left, credits on the right ---------- */}
        <div className="mf-bar">
          <div className="mf-post-head">
            <MaybeLink to={profileHref} className="mf-post-avatar" style={noUnderline} aria-label={`${authorName}'s profile`}>
              <Avatar user={author} size={36} />
            </MaybeLink>

            <div className="mf-post-who">
              <MaybeLink to={profileHref} className="mf-post-name" style={noUnderline}>
                {authorName}
              </MaybeLink>
              <time
                className="mf-post-time"
                dateTime={created ? created.toISOString() : undefined}
                title={created ? created.toLocaleString() : ''}
              >
                {timeAgo(post.created)}
              </time>
            </div>

            {post.licenseType && (
              <span className={`mf-badge mf-badge--${String(post.licenseType).toLowerCase()}`}>
                {post.licenseType === 'Private' && <LockRoundedIcon />}
                {post.licenseType === 'Public' && <PublicRoundedIcon />}
                <span className="mf-badge-t">{post.licenseType}</span>
              </span>
            )}

            {isCreator && (
              <div className="mf-menu" ref={menuRef}>
                <button
                  type="button"
                  className="mf-menu-btn"
                  aria-label="Post options"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  onClick={() => setMenuOpen(o => !o)}
                >
                  <MoreHorizRoundedIcon />
                </button>
                {menuOpen && (
                  <div className="mf-menu-pop" role="menu">
                    <button type="button" role="menuitem" className="mf-menu-item" onClick={editPost}>
                      <EditRoundedIcon /> Edit post
                    </button>
                    <button type="button" role="menuitem" className="mf-menu-item mf-menu-item--danger" onClick={deletePost}>
                      <DeleteOutlineRoundedIcon /> Delete post
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <button
            type="button"
            className="mf-act mf-credits"
            onClick={giveCredits}
            aria-label={`Give 1 credit (${credits} so far)`}
            title="Give 1 credit"
          >
            <StarRoundedIcon />
            <span>{formatCount(credits)}</span>
            <small>Credits</small>
          </button>
        </div>

        {/* ---------- Comments panel (opens from the comment button) ---------- */}
        {expanded && commentsOpen && (
          <>
            <div className="mf-cm-back" onClick={() => setCommentsOpen(false)} aria-hidden="true" />
            <section className="mf-cm" role="dialog" aria-label={`Comments (${comments.length})`}>
              <div className="mf-cm-head">
                <h4>Comments<span>{comments.length}</span></h4>
                <button
                  type="button"
                  className="mf-modal-x"
                  aria-label="Close comments"
                  onClick={() => setCommentsOpen(false)}
                >
                  <CloseRoundedIcon />
                </button>
              </div>

              <div className="mf-cm-list">
                {comments.length === 0 && (
                  <p className="mf-cm-empty">No comments yet. Start the conversation!</p>
                )}
                {comments.map(c => {
                  const cAuthor = c.expand?.author;
                  return (
                    <div key={c.id} className="mf-comment">
                      <Avatar user={cAuthor} size={32} />
                      <div className="mf-comment-bubble">
                        <div>
                          <span className="mf-comment-name">{getDisplayName(cAuthor)}</span>
                          <span className="mf-comment-time">{timeAgo(c.created)}</span>
                        </div>
                        <div className="mf-comment-text">{c.content}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {user ? (
                <form
                  className="mf-comment-form"
                  onSubmit={(e) => { e.preventDefault(); submitComment(); }}
                >
                  <Avatar user={user} size={32} />
                  <input
                    className="mf-input"
                    value={newComment}
                    onChange={e => setNewComment(e.target.value)}
                    placeholder="Write a comment…"
                    aria-label="Write a comment"
                    maxLength={1000}
                  />
                  <button
                    type="submit"
                    className="mf-send"
                    disabled={!newComment.trim() || posting}
                    aria-label="Send comment"
                  >
                    <SendRoundedIcon />
                  </button>
                </form>
              ) : (
                <p className="mf-login-hint">Log in to join the conversation.</p>
              )}
            </section>
          </>
        )}
      </div>

      {/* ---------- Reshare dialog (outside the stage so it covers the whole screen) ---------- */}
      {showReshare && !reshared && (
        <div
          className="mf-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Reshare post"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setShowReshare(false); }}
        >
          <div className="mf-modal-card">
            <div className="mf-modal-head">
              <h4>Reshare post</h4>
              <button
                type="button"
                className="mf-modal-x"
                aria-label="Close"
                onClick={() => setShowReshare(false)}
              >
                <CloseRoundedIcon />
              </button>
            </div>

            <div className="mf-quote">
              <Avatar user={author} size={36} />
              <div style={{ minWidth: 0 }}>
                <strong>{authorName}</strong>
                <p>{locked ? 'Private post' : (contentText || (items.length ? 'Shared a post' : ''))}</p>
              </div>
            </div>

            <textarea
              className="mf-textarea"
              autoFocus
              value={reshareText}
              onChange={e => setReshareText(e.target.value)}
              placeholder="Add a comment or emoji…"
            />

            <div className="mf-modal-actions">
              <button type="button" className="mf-btn mf-btn--ghost" onClick={() => setShowReshare(false)}>
                Cancel
              </button>
              <button type="button" className="mf-btn mf-btn--primary" onClick={confirmReshare}>
                Reshare now
              </button>
            </div>
          </div>
        </div>
      )}
    </article>
  );
}